/**
 * hydrology_bake — the WORLD HYDROLOGY BAKE (terrain wave 2026-09-08):
 * rivers, waterfalls and lakes extracted ONCE per world seed from the
 * plateau'd terrain, served as static data (`public/hydrology/<fnv(seed)>.json`)
 * and carved into the ground at query time by the river layer in
 * `shared/terrain.ts` (`setRiverNetwork` → `landformHeight`).
 *
 * Why a bake and not a runtime pass: the regional lake bake documents its own
 * limit — a 1.5 km window "can't know continental drainage". Drainage is a
 * property of the whole basin, so it is computed here over 56 km windows
 * around every settled region (the parts of the world people actually walk
 * and fly), with the standard GIS recipe: fill sinks (Priority-Flood) →
 * D8 receivers → flow accumulation → threshold → trace the network
 * (ArcGIS "Identifying stream networks"; Barnes et al. Priority-Flood).
 *
 * Settlements are protected BEFORE extraction: every plateau disc + ring is
 * raised by a bump in the bake grid, so no drainage path ever crosses a town
 * (the runtime carve also runs before the plateau layer, so a disc always
 * wins). Channel width and depth follow Leopold & Maddock's downstream
 * hydraulic geometry — width ∝ Q^0.5, depth ∝ Q^0.4 — with drainage area
 * standing in for discharge. A reach whose water surface drops ≥ FALL_MIN m
 * over one segment is a WATERFALL node (the carve builds the ledge).
 *
 * Pure: no DOM, no Three.js — node-verifiable and worker-safe.
 */
import { analyzeHydrology } from 'enginex/terrain/TerrainHydrology.js';
import { plateauSites } from '@shared/terrain.js';
import { settlementRegionCenters, SETTLEMENT_REGION_R } from '@shared/settlements.js';

/** Grid cell (m) of the bake. */
export const HYDRO_CELL = 64;
/** Half-extent (m) of a bake window around a settled-region centre. */
export const HYDRO_WINDOW_HALF = SETTLEMENT_REGION_R + 12_000;
/** Upslope cells that make a stream (≈ 4.1 km² at 64 m cells). */
export const STREAM_THRESHOLD_CELLS = 1000;
/** Water-surface drop (m) over one reach that makes a waterfall … */
export const FALL_MIN = 5;
/** … provided the reach is steep enough (drop / run ≥ this, ≈ 3°): a gentle
 *  300 m reach losing 5 m is a rapid, not a ledge. */
export const FALL_MIN_GRADE = 0.05;
/**
 * Closed basins deeper than this are BREACHED, not filled: the river keeps a
 * lake of this depth on the basin floor and cuts a gorge through the rim
 * (Lindsay-style depression breaching — the erosional answer to a pit — instead
 * of the thousand fjord-lakes a noise mountain belt otherwise fills).
 */
export const LAKE_KEEP_DEPTH = 12;
/** Douglas–Peucker tolerance (m) for the shipped polylines. */
export const RIVER_SIMPLIFY_TOL = 8;
/** Free-board: the water surface sits this far below the natural bank. */
export const FREEBOARD = 0.6;
/** Plateau bump (m) added to the bake grid so drainage routes around towns. */
export const PLATEAU_BUMP = 60;
/** Margin (m) beyond a plateau disc that the bump (and so every river) keeps clear. */
export const PLATEAU_CLEAR = 300;
/** Pooled depth (m) that makes a lake cell, and the smallest lake (cells). */
export const LAKE_MIN_DEPTH = 1.0;
export const LAKE_MIN_CELLS = 24;
/** Lakes shallower than this are left to the runtime regional bake (which
 *  fills any pit ≤ 5 m on its own) and not listed. */
export const LAKE_REPORT_DEPTH = 4;

export interface HydroWindow { cx: number; cz: number; half: number; rivers: number[][]; lakes: HydroLake[] }
/** A lake summary — centroid, spill level, area (km²), max depth, bbox. */
export interface HydroLake { cx: number; cz: number; level: number; area: number; depth: number; x0: number; z0: number; x1: number; z1: number }
export interface HydroFile { v: 1; seed: string; cell: number; sea: number; windows: HydroWindow[] }

/** Per-point stride of a river's flat array: x, z, water, width, depth, flag. */
export const RIVER_STRIDE = 6;
/** flag bit: a waterfall lip lies on the segment that STARTS at this point. */
export const RIVER_FLAG_FALL = 1;
/** Flag 2 (set at DECODE, never baked): the point lies on still water — at the sea or on
 *  a lake at its level — so its reach cuts its channel but never raises the ground. */
export const RIVER_FLAG_NORAISE = 2;
/** Flag 4 (set at DECODE): the point lies ON a lake at its level — the lake renders the
 *  water there, so the ribbon builder hands the surface to it (no double sheet). */
export const RIVER_FLAG_LAKE = 4;

export interface HydroWindowSpec { cx: number; cz: number; half: number }

/**
 * The bake windows of a seed: one per settled region, with OVERLAPPING windows
 * merged into one square (two grids over the same ground would trace two
 * slightly different networks and carve crossing channels).
 */
export function hydrologyWindows(seed: string): HydroWindowSpec[] {
  const boxes = settlementRegionCenters(seed).map(([cx, cz]) => ({ x0: cx - HYDRO_WINDOW_HALF, z0: cz - HYDRO_WINDOW_HALF, x1: cx + HYDRO_WINDOW_HALF, z1: cz + HYDRO_WINDOW_HALF }));
  let merged = true;
  while (merged) {
    merged = false;
    for (let i = 0; i < boxes.length && !merged; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const a = boxes[i], b = boxes[j];
        if (a.x0 > b.x1 || b.x0 > a.x1 || a.z0 > b.z1 || b.z0 > a.z1) continue;
        boxes[i] = { x0: Math.min(a.x0, b.x0), z0: Math.min(a.z0, b.z0), x1: Math.max(a.x1, b.x1), z1: Math.max(a.z1, b.z1) };
        boxes.splice(j, 1);
        merged = true;
        break;
      }
    }
  }
  return boxes.map((b) => {
    const half = Math.max(b.x1 - b.x0, b.z1 - b.z0) / 2;
    return { cx: (b.x0 + b.x1) / 2, cz: (b.z0 + b.z1) / 2, half };
  });
}

/** Channel width (m) from drainage area (km²): Leopold–Maddock w ∝ Q^0.5. */
export function riverWidth(areaKm2: number): number {
  return Math.min(80, Math.max(5, 3.5 * Math.sqrt(areaKm2)));
}
/** Channel depth (m) from drainage area (km²): Leopold–Maddock d ∝ Q^0.4. */
export function riverDepth(areaKm2: number): number {
  return Math.min(7, Math.max(0.8, 0.55 * Math.pow(areaKm2, 0.4)));
}

function smooth01(t: number): number { return t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t); }

/** Height sampler contract: the plateau'd terrain (terrainHeight). */
export type HeightSampler = (x: number, z: number) => number;

/**
 * Bake one window. `sample` is the plateau'd `terrainHeight`; the plateau bump
 * is added here from `plateauSites(seed)`.
 */
export function bakeHydrologyWindow(seed: string, spec: HydroWindowSpec, sample: HeightSampler, sea: number, cell = HYDRO_CELL, threshold = STREAM_THRESHOLD_CELLS): HydroWindow {
  const n = Math.floor((spec.half * 2) / cell) + 1;
  const x0 = spec.cx - spec.half;
  const z0 = spec.cz - spec.half;
  const total = n * n;
  const raw = new Float32Array(total);
  const grid = new Float32Array(total);
  /** Cells under the full bump (disc + clear margin): they contribute NO rain below. */
  const bumped = new Uint8Array(total);
  // Plateaus inside (or near) the window: bump zone = disc + clear margin.
  const plats = plateauSites(seed).filter((p) => Math.abs(p.x - spec.cx) < spec.half + p.r + PLATEAU_CLEAR && Math.abs(p.z - spec.cz) < spec.half + p.r + PLATEAU_CLEAR);
  for (let row = 0; row < n; row++) {
    const z = z0 + row * cell;
    for (let col = 0; col < n; col++) {
      const x = x0 + col * cell;
      const h = sample(x, z);
      const i = row * n + col;
      raw[i] = h;
      let bump = 0;
      for (const p of plats) {
        const d = Math.sqrt((x - p.x) * (x - p.x) + (z - p.z) * (z - p.z));
        const outer = p.r + PLATEAU_CLEAR;
        if (d >= outer + 200) continue;
        // full bump to the clear margin, fading over the next 200 m
        const w = d <= outer ? 1 : 1 - smooth01((d - outer) / 200);
        if (w > bump) bump = w;
      }
      grid[i] = h + bump * PLATEAU_BUMP;
      if (bump >= 1) bumped[i] = 1;
    }
  }
  const hyd = analyzeHydrology(grid, n, 1e-3);
  const { filled, receivers, order } = hyd;
  // Capital wave (2026-09-15): a capital's plateau is a flat mesa of ~8,800 cells —
  // its own unit rain crossed the 1,000-cell stream threshold ON the plateau (a
  // river traced 900 m inside the disc) and would seed a moat of streams at its
  // rim. Bumped cells contribute no rain; everything else accumulates as before
  // (the kernel's unit-rain pass, re-run here in its own descending-height order).
  const accumulation = new Float32Array(total);
  for (let i = 0; i < total; i++) accumulation[i] = bumped[i] ? 0 : 1;
  for (let k = 0; k < total; k++) {
    const i = order[k];
    const r = receivers[i];
    if (r >= 0 && r !== i) accumulation[r] += accumulation[i];
  }

  // ── Basins: fill the shallow ones, BREACH the deep ones ────────────────────
  // `hold[i]` = the water level a river may keep through cell i: the spill
  // level for a basin ≤ LAKE_KEEP_DEPTH (a lake), else floor + LAKE_KEEP_DEPTH
  // (a lake on the floor, a gorge through the rim). Outside basins: the ground.
  const hold = new Float32Array(total);
  const compId = new Int32Array(total).fill(-1);
  const compFloor: number[] = [];
  const compDepth: number[] = [];
  const compLevel: number[] = [];
  {
    const seen = new Uint8Array(total);
    const stack: number[] = [];
    for (let s = 0; s < total; s++) {
      if (seen[s] || filled[s] - grid[s] < LAKE_MIN_DEPTH) continue;
      const id = compFloor.length;
      let floor = Infinity, depth = 0, level = -Infinity;
      stack.push(s); seen[s] = 1;
      while (stack.length) {
        const i = stack.pop()!;
        compId[i] = id;
        if (grid[i] < floor) floor = grid[i];
        const d = filled[i] - grid[i];
        if (d > depth) depth = d;
        if (filled[i] > level) level = filled[i];
        const col = i % n;
        const nb = [i - 1, i + 1, i - n, i + n];
        for (const j of nb) {
          if (j < 0 || j >= total || seen[j]) continue;
          if ((j === i - 1 && col === 0) || (j === i + 1 && col === n - 1)) continue;
          if (filled[j] - grid[j] < LAKE_MIN_DEPTH) continue;
          seen[j] = 1; stack.push(j);
        }
      }
      compFloor.push(floor); compDepth.push(depth); compLevel.push(level);
    }
  }
  for (let i = 0; i < total; i++) {
    const c = compId[i];
    if (c < 0) { hold[i] = grid[i]; continue; }
    hold[i] = compDepth[c] <= LAKE_KEEP_DEPTH ? compLevel[c] : Math.max(grid[i], compFloor[c] + LAKE_KEEP_DEPTH);
  }

  // ── Rivers: trace from every stream head down the receivers ────────────────
  const mask = new Uint8Array(total);
  for (let i = 0; i < total; i++) if (accumulation[i] >= threshold && raw[i] > sea) mask[i] = 1;
  // donors count per masked cell
  const donors = new Uint8Array(total);
  for (let i = 0; i < total; i++) {
    if (!mask[i]) continue;
    const r = receivers[i];
    if (r >= 0 && mask[r]) donors[r]++;
  }
  const claimed = new Int32Array(total).fill(-1);
  const rivers: number[][] = [];
  const cellArea = (cell * cell) / 1e6; // km²
  for (let head = 0; head < total; head++) {
    if (!mask[head] || donors[head] !== 0) continue;
    const pts: number[] = [];
    let i = head;
    let water = Infinity;
    let guard = 0;
    while (i >= 0 && guard++ < total) {
      const row = (i / n) | 0;
      const col = i % n;
      const x = x0 + col * cell;
      const z = z0 + row * cell;
      const area = accumulation[i] * cellArea;
      // Water surface: the held level minus free-board, MONOTONE downstream —
      // the min is what breaches a deep basin's rim (the level set on the floor
      // is carried through the rim cells, and the carve cuts the gorge).
      let w = hold[i] - FREEBOARD;
      if (w > water) w = water;
      water = w;
      pts.push(x, z, w, riverWidth(area), riverDepth(area), 0);
      if (claimed[i] >= 0) break;       // joined a river traced earlier — this is the junction point
      claimed[i] = rivers.length;
      const r = receivers[i];
      if (r < 0) break;                  // window border outlet
      if (raw[r] <= sea) {               // entering the sea: one last point at the shore, done
        const rr = (r / n) | 0; const rc = r % n;
        pts.push(x0 + rc * cell, z0 + rr * cell, Math.min(water, sea - FREEBOARD), riverWidth(accumulation[r] * cellArea), riverDepth(accumulation[r] * cellArea), 0);
        break;
      }
      i = r;
    }
    if (pts.length >= 2 * RIVER_STRIDE) rivers.push(pts);
  }

  // ── Shape: one Chaikin pass (rounds the D8 staircase), then Douglas–Peucker ─
  const shaped = rivers.map((r) => simplifyRiver(chaikin(r), RIVER_SIMPLIFY_TOL));
  // ── Waterfalls: flag reaches whose water drops ≥ FALL_MIN ─────────────────
  for (const r of shaped) {
    const m = r.length / RIVER_STRIDE;
    for (let k = 0; k < m - 1; k++) {
      const a = k * RIVER_STRIDE, b = a + RIVER_STRIDE;
      const drop = r[a + 2] - r[b + 2];
      const run = Math.hypot(r[b] - r[a], r[b + 1] - r[a + 1]);
      if (drop >= FALL_MIN && run > 0 && drop / run >= FALL_MIN_GRADE) r[a + 5] |= RIVER_FLAG_FALL;
    }
  }

  // ── Lakes: the basins, at their held (post-breach) levels ──────────────────
  const lakes: HydroLake[] = [];
  {
    const count = new Int32Array(compFloor.length);
    const sx = new Float64Array(compFloor.length), sz = new Float64Array(compFloor.length);
    const bx0 = new Float64Array(compFloor.length).fill(Infinity), bz0 = new Float64Array(compFloor.length).fill(Infinity);
    const bx1 = new Float64Array(compFloor.length).fill(-Infinity), bz1 = new Float64Array(compFloor.length).fill(-Infinity);
    const wet = new Uint8Array(compFloor.length);
    for (let i = 0; i < total; i++) {
      const c = compId[i];
      if (c < 0 || raw[i] <= sea) continue;
      const level = (compDepth[c] <= LAKE_KEEP_DEPTH ? compLevel[c] : compFloor[c] + LAKE_KEEP_DEPTH) - FREEBOARD;
      if (grid[i] >= level) continue; // above the held water: dry ground of a breached basin
      const row = (i / n) | 0; const col = i % n;
      const x = x0 + col * cell; const z = z0 + row * cell;
      count[c]++; sx[c] += x; sz[c] += z; wet[c] = 1;
      if (x < bx0[c]) bx0[c] = x; if (x > bx1[c]) bx1[c] = x; if (z < bz0[c]) bz0[c] = z; if (z > bz1[c]) bz1[c] = z;
    }
    for (let c = 0; c < compFloor.length; c++) {
      if (!wet[c] || count[c] < LAKE_MIN_CELLS) continue;
      const depth = Math.min(compDepth[c], LAKE_KEEP_DEPTH);
      if (depth <= LAKE_REPORT_DEPTH) continue;
      const level = (compDepth[c] <= LAKE_KEEP_DEPTH ? compLevel[c] : compFloor[c] + LAKE_KEEP_DEPTH) - FREEBOARD;
      lakes.push({ cx: sx[c] / count[c], cz: sz[c] / count[c], level, area: count[c] * cellArea, depth, x0: bx0[c], z0: bz0[c], x1: bx1[c], z1: bz1[c] });
    }
  }
  return { cx: spec.cx, cz: spec.cz, half: spec.half, rivers: shaped, lakes };
}

/** One Chaikin corner-cutting pass over a flat river array (all channels interpolate). */
export function chaikin(pts: number[]): number[] {
  const m = pts.length / RIVER_STRIDE;
  if (m < 3) return pts.slice();
  const out: number[] = [];
  const push = (a: number, b: number, t: number): void => {
    for (let c = 0; c < RIVER_STRIDE; c++) {
      const va = pts[a * RIVER_STRIDE + c], vb = pts[b * RIVER_STRIDE + c];
      out.push(c === 5 ? 0 : va + (vb - va) * t);
    }
  };
  for (let c = 0; c < RIVER_STRIDE; c++) out.push(pts[c]); // keep the head exactly
  for (let k = 0; k < m - 1; k++) { push(k, k + 1, 0.25); push(k, k + 1, 0.75); }
  for (let c = 0; c < RIVER_STRIDE; c++) out.push(pts[(m - 1) * RIVER_STRIDE + c]); // and the mouth
  return out;
}

/** Douglas–Peucker in XZ (tolerance m) — keeps every point's own water/width/depth. */
export function simplifyRiver(pts: number[], tol = 4): number[] {
  const m = pts.length / RIVER_STRIDE;
  if (m <= 2) return pts.slice();
  const keep = new Uint8Array(m);
  keep[0] = 1; keep[m - 1] = 1;
  const stack: Array<[number, number]> = [[0, m - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    if (b - a < 2) continue;
    const ax = pts[a * RIVER_STRIDE], az = pts[a * RIVER_STRIDE + 1];
    const bx = pts[b * RIVER_STRIDE], bz = pts[b * RIVER_STRIDE + 1];
    const dx = bx - ax, dz = bz - az;
    const len2 = dx * dx + dz * dz;
    let worst = -1, wd = tol;
    for (let k = a + 1; k < b; k++) {
      const px = pts[k * RIVER_STRIDE] - ax, pz = pts[k * RIVER_STRIDE + 1] - az;
      const t = len2 > 0 ? Math.max(0, Math.min(1, (px * dx + pz * dz) / len2)) : 0;
      const ex = px - dx * t, ez = pz - dz * t;
      const d = Math.sqrt(ex * ex + ez * ez);
      if (d > wd) { wd = d; worst = k; }
    }
    if (worst >= 0) { keep[worst] = 1; stack.push([a, worst], [worst, b]); }
  }
  const out: number[] = [];
  for (let k = 0; k < m; k++) if (keep[k]) for (let c = 0; c < RIVER_STRIDE; c++) out.push(pts[k * RIVER_STRIDE + c]);
  return out;
}

/** Stable fnv1a-32 of the seed — the hydrology file name carries it. */
export function hydrologyFileFor(seed: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return `${(h >>> 0).toString(16)}.json`;
}

/**
 * Compact JSON. Rivers are encoded as flat INTEGER arrays: per point
 * dx, dz (0.1 m, deltas from the previous point; the first point absolute),
 * dWater (0.01 m delta; first absolute), width (0.1 m), depth (0.1 m), flag.
 * `decodeRiver` reverses it; every thread decodes identically (same integers,
 * same arithmetic), so the carve is bit-identical across main and workers.
 */
export function serializeHydrology(file: HydroFile): string {
  const q = (v: number, s: number): number => Math.round(v * s) / s;
  const windows = file.windows.map((w) => ({
    cx: q(w.cx, 10), cz: q(w.cz, 10), half: w.half,
    rivers: w.rivers.map(encodeRiver),
    lakes: w.lakes.map((l) => ({ cx: q(l.cx, 10), cz: q(l.cz, 10), level: q(l.level, 100), area: q(l.area, 1000), depth: q(l.depth, 100), x0: l.x0, z0: l.z0, x1: l.x1, z1: l.z1 })),
  }));
  return JSON.stringify({ v: 1, seed: file.seed, cell: file.cell, sea: q(file.sea, 100), windows });
}

export function encodeRiver(r: number[]): number[] {
  const m = r.length / RIVER_STRIDE;
  const out: number[] = [];
  let px = 0, pz = 0, pw = 0;
  for (let k = 0; k < m; k++) {
    const o = k * RIVER_STRIDE;
    const x = Math.round(r[o] * 10), z = Math.round(r[o + 1] * 10), w = Math.round(r[o + 2] * 100);
    out.push(x - px, z - pz, w - pw, Math.round(r[o + 3] * 10), Math.round(r[o + 4] * 10), r[o + 5] | 0);
    px = x; pz = z; pw = w;
  }
  return out;
}

export function decodeRiver(e: ArrayLike<number>): number[] {
  const m = Math.floor(e.length / RIVER_STRIDE);
  const out = new Array<number>(m * RIVER_STRIDE);
  let x = 0, z = 0, w = 0;
  for (let k = 0; k < m; k++) {
    const o = k * RIVER_STRIDE;
    x += e[o]; z += e[o + 1]; w += e[o + 2];
    out[o] = x / 10; out[o + 1] = z / 10; out[o + 2] = w / 100;
    out[o + 3] = e[o + 3] / 10; out[o + 4] = e[o + 4] / 10; out[o + 5] = e[o + 5];
  }
  return out;
}

/** Every river of a fetched file, decoded to the registry's flat format. */
export function decodeHydrologyRivers(file: HydroFile): number[][] {
  const out: number[][] = [];
  const all = file.windows.flatMap((w) => w.lakes);
  for (const w of file.windows) {
    // Lakes whose box meets this window (a lake near a border belongs to both).
    const lakes = all.filter((l) => l.x1 >= w.cx - w.half - 1000 && l.x0 <= w.cx + w.half + 1000 && l.z1 >= w.cz - w.half - 1000 && l.z0 <= w.cz + w.half + 1000);
    for (const r of w.rivers) { const pts = decodeRiver(r); flagStillWater(pts, lakes, file.sea); out.push(pts); }
  }
  return out;
}

/**
 * RIVER_FLAG_NORAISE on every point at the sea (water ≤ sea − FREEBOARD) or ON a lake
 * (inside its box at the lake's own held level): the terrain modifier of such a reach
 * only cuts, so a river crossing a lake never builds a bar under its surface and a
 * mouth never dams the sea. A river descending INTO a lake through its box is above
 * the level and stays a raising reach (it needs a bed on the slope).
 */
export function flagStillWater(pts: number[], lakes: readonly HydroLake[], sea: number): void {
  for (let i = 0; i < pts.length; i += RIVER_STRIDE) {
    const x = pts[i], z = pts[i + 1], water = pts[i + 2];
    let still = water <= sea - FREEBOARD + 1e-3, onLake = false;
    for (const l of lakes) {
      if (x >= l.x0 && x <= l.x1 && z >= l.z0 && z <= l.z1 && Math.abs(water - (l.level - FREEBOARD)) <= 0.35) { still = true; onLake = true; break; }
    }
    if (still) pts[i + 5] |= RIVER_FLAG_NORAISE;
    if (onLake) pts[i + 5] |= RIVER_FLAG_LAKE;
  }
}

/** Validate a fetched file; returns a problem string or null. */
export function hydrologyFileProblem(f: unknown, seed: string): string | null {
  const x = f as Partial<HydroFile> | null;
  if (!x || typeof x !== 'object') return 'not an object';
  if (x.v !== 1) return `unknown version ${String(x.v)}`;
  if (x.seed !== seed) return `seed mismatch (${String(x.seed)} ≠ ${seed})`;
  if (!Array.isArray(x.windows)) return 'no windows';
  for (const w of x.windows) for (const r of w.rivers) if (!Array.isArray(r) || r.length % RIVER_STRIDE !== 0 || r.length < 2 * RIVER_STRIDE) return 'malformed river';
  return null;
}
